import { PageTypeEnum } from '@doltech/utils/lib/constants';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import * as React from 'react';
import styled from 'styled-components';
import { Breadcrumb } from '../../../figma/Breadcrumb';
import renderNode, { Div } from '../../../figma/renderNode';
import { ResponsiveTypography } from '../../../figma/Typography/v3/ResponsiveTypography';
import { DecorateArrow } from './decorateSVG/DecorateArrow';
import { DecoratePolish } from './decorateSVG/DecoratePolish';
import { DecorateWave } from './decorateSVG/DecorateWave';
import { ContactDol } from '../../../figma/ContactDol/ContactDol';
import { LandingPromotionFreeTestBanner } from './PromotionTypeForLandingPage/LandingPromotionFreeTestBanner';
import { JuniorPromotionBanner } from '../../../figma/Layout/Landing/ui/JuniorPromotionBanner/JuniorPromotionBanner';

const Main = styled.div`
  .blog-content-body {
    padding-top: 64px;
    margin: 0 auto;
  }

  .blog-content-header {
    background: #fff9f1;
  }

  .logo-dol {
    width: 108px;
  }

  .blog-content-header-wrapper {
    padding: 32px 16px;
    display: grid;
    grid-auto-rows: max-content;
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
    text-align: left;
    margin: 0 auto;

    ${fromScreen(458)} {
      padding: 32px 0;
      width: 425px;
    }

    ${fromScreen(776)} {
      padding: 32px 16px;
      width: 712px;
      text-align: center;
      min-height: 348px;
    }

    ${fromScreen(1144)} {
      gap: 48px;
      width: 1080px;
      min-height: 402px;
    }
  }

  .header-info {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
    margin: 0 auto;
    position: relative;

    ${fromScreen(458)} {
      width: 425px;
    }

    ${fromScreen(776)} {
      width: 500px;
    }

    ${fromScreen(1144)} {
      gap: 32px;
      width: 662px;
    }
  }

  .underline-text {
    padding: 0 2px;
    background-image: linear-gradient(
      120deg,
      rgb(255, 153, 31, 0.35) 0%,
      rgb(255, 153, 31, 0.35) 100%
    );
    background-repeat: no-repeat;
    background-size: 100% 0.2em;
    background-position: 0 88%;
    transition: background-size 0.25s ease-out;
  }

  .decorate-wave,
  .decorate-polish,
  .decorate-arrow {
    position: absolute;
    display: none;

    ${fromScreen(776)} {
      display: block;
    }
  }

  .decorate-wave {
    top: 148px;
    left: -90px;

    ${fromScreen(1144)} {
      top: 138px;
      left: -182px;
    }
  }

  .decorate-polish {
    top: -24px;
    right: 10px;

    ${fromScreen(1144)} {
      top: -44px;
      right: 0;
    }
  }

  .decorate-arrow {
    right: -18px;
    bottom: -66px;

    ${fromScreen(1144)} {
      right: -96px;
      bottom: -40px;
    }
  }

  .breadcrumb-item {
    > * {
      text-transform: none;
    }
  }
`;

const getPageHeader = ({ pageType, pageProps }) => {
  const description = pageProps?.seoData?.dol?.description || '';
  const defaultBreadcrumb = [
    {
      breadcrumbName: 'Trang chủ',
      path: '/',
    },
    {
      breadcrumbName: 'Kiến thức IELTS',
      path: pageProps?.internalPageLink?.staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url,
    },
  ];

  switch (pageType) {
    case PageTypeEnum.LANDING_IELTS_KNOWLEDGE: {
      return {
        title: (
          <>
            Kiến thức <span className="underline-text">IELTS</span>
          </>
        ),
        description,
        breadcrumbRoutes: defaultBreadcrumb,
      };
    }
    case PageTypeEnum.LANDING_IELTS_REVIEW: {
      // http://localhost:8000/review-va-chia-se-kinh-nghiem

      return {
        title: (
          <>
            <span className="underline-text">Tổng quan</span> về IELTS
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'Tổng quan về IELTS',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_READING_IELTS_BLOG: {
      // http://localhost:8000/luyen-thi-ielts/ielts-reading1
      return {
        title: (
          <>
            IELTS <span className="underline-text">Reading</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Reading',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_WRITING_IELTS_BLOG: {
      // http://localhost:8000/ielts-writing1
      return {
        title: (
          <>
            IELTS <span className="underline-text">Writing</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Writing',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_SPEAKING_IELTS_BLOG: {
      // http://localhost:8000/ielts-speaking-blog3
      return {
        title: (
          <>
            IELTS <span className="underline-text">Speaking</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Speaking',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_LISTENING_IELTS_BLOG: {
      // http://localhost:8000/luyen-thi-ielts/ielts-listening1
      return {
        title: (
          <>
            IELTS <span className="underline-text">Listening</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Listening',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_GRAMMAR_IELTS_BLOG: {
      // http://localhost:8000/ielts-grammar
      return {
        title: (
          <>
            <span className="underline-text">Ngữ pháp</span> IELTS
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'Ngữ pháp IELTS',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_VOCAB_IELTS_BLOG: {
      // http://localhost:8000/tong-hop-tu-vung-ielts1
      return {
        title: (
          <>
            <span className="underline-text">Từ vựng</span> IELTS
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'Từ vựng IELTS',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_WRITING_SAMPLE: {
      // http://localhost:8000/ielts-writing-sample
      return {
        title: (
          <>
            IELTS Writing <span className="underline-text">Sample</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Writing Sample',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_WRITING_SAMPLE_T2G: {
      // http://localhost:8000/ielts-writing-task-general-sample1
      return {
        title: (
          <>
            Bài mẫu IELTS Writing
            <br /> Task 1 General
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Writing Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_WRITING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Writing Task 1 General',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_WRITING_SAMPLE_T1: {
      // http://localhost:8000/ielts-writing-task-1-sample
      return {
        title: (
          <>
            Bài mẫu IELTS Writing
            <br /> Task 1 Academic
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Writing Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_WRITING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Writing Task 1 Academic',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_WRITING_SAMPLE_T2A: {
      // http://localhost:8000/ielts-writing-task-2-academic-sample
      return {
        title: (
          <>
            Bài mẫu IELTS Writing
            <br /> Task 2 Academic
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Writing Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_WRITING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Writing Task 2 Academic',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_SPEAKING_SAMPLE: {
      // http://localhost:8000/ielts-speaking1
      return {
        title: (
          <>
            IELTS Speaking <span className="underline-text">Sample</span>
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Speaking Sample',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_SPEAKING_SAMPLE_P1: {
      // http://localhost:8000/ielts-speaking-part-1-sample
      return {
        title: (
          <>
            Bài mẫu IELTS Speaking
            <br /> Part 1
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Speaking Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_SPEAKING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Speaking Part 1',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_SPEAKING_SAMPLE_P2: {
      // http://localhost:8000/ielts-speaking-part-1-sample
      return {
        title: (
          <>
            Bài mẫu IELTS Speaking
            <br /> Part 2
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Speaking Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_SPEAKING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Speaking Part 2',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_SPEAKING_SAMPLE_P3: {
      // http://localhost:8000/ielts-speaking-part-1-sample
      return {
        title: (
          <>
            Bài mẫu IELTS Speaking
            <br /> Part 3
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'IELTS Speaking Sample',
            path: pageProps?.internalPageLink?.staticPageLink?.LANDING_SPEAKING_SAMPLE?.url,
          },
          {
            breadcrumbName: 'Bài mẫu IELTS Speaking Part 3',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    case PageTypeEnum.LANDING_BOOK_REVIEW: {
      // http://localhost:8000/review-tai-lieu-va-website-tu-hoc-2

      return {
        title: (
          <>
            <span className="underline-text">Tài liệu</span> IELTS
          </>
        ),
        description,
        breadcrumbRoutes: [
          ...defaultBreadcrumb,
          {
            breadcrumbName: 'Tài liệu IELTS',
            path: pageProps?.seoData?.url,
          },
        ],
      };
    }
    default:
  }
  return {
    title: pageType,
    description: 'Please support this page type',
    breadcrumbRoutes: [
      {
        breadcrumbName: 'Trang chủ',
        path: '/',
      },
    ],
  };
};

export const BlogHeader = ({ pageProps }) => {
  const { title, description, breadcrumbRoutes } = getPageHeader({
    pageProps,
    pageType: pageProps?.seoData?.type,
  });
  return (
    <div className="blog-content-header">
      <div className="blog-content-header-wrapper">
        <Breadcrumb useFor="landing-blog" routes={breadcrumbRoutes} seoData={pageProps?.seoData} />
        <div className="header-info">
          <ResponsiveTypography.Title
            className="title"
            breakpoints={[0, 458, 1144]}
            variant={['bold/32-40', 'bold/40-48', 'bold/48-57.6']}
            level={1}
          >
            {title}
          </ResponsiveTypography.Title>
          <ResponsiveTypography.Paragraph
            color="black80"
            className="description"
            breakpoints={[0, 1144]}
            variant={['regular/16-24', 'regular/20-28']}
          >
            {description}
          </ResponsiveTypography.Paragraph>

          <div className="decorate-wave">
            <DecorateWave />
          </div>
          <div className="decorate-polish">
            <DecoratePolish />
          </div>
          <div className="decorate-arrow">
            <DecorateArrow />
          </div>
        </div>
      </div>
    </div>
  );
};

export const LayoutLandingBlog = (props) => {
  const { renderHeader, renderFooter, children, className = undefined } = props;
  return (
    <Main className={className}>
      <JuniorPromotionBanner />
      <ContactDol />
      {renderNode(Div, renderHeader, {})}
      <div className="blog-content-body">{children}</div>
      <LandingPromotionFreeTestBanner />
      {renderNode(Div, renderFooter, {})}
    </Main>
  );
};
